<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { vOnClickOutside } from '@vueuse/components'
import { toKebabCase } from '@/shared/utils/format'
import type { Client } from '../models/client.models'

import BaseButton from '@/shared/components/button/BaseButton.vue'
import CaretIcon from '@/shared/components/icon/CaretIcon.vue'
import BaseDropdown from '@/shared/components/dropdown/BaseDropdown.vue'

interface Props {
  title: string
  clients: Client[]
}

interface Emits {
  (e: 'change'): void
}

const emits = defineEmits<Emits>()
const model = defineModel<Client | undefined>({ required: true })
const props = defineProps<Props>()
  
const filter = ref<string>('')
const isActive = ref(false)
const name = toKebabCase(props.title)

const loadDefault = () => {
  if (model.value) {
    const client = props.clients.find(
      (client: Client) => client.clientKey === model.value?.clientKey,
    )
    filter.value = client ? client.clientName : ''
  }
}

const filteredOptions = computed((): Client[] => {
  return filter.value === ''
    ? props.clients || []
    : props.clients?.filter(
        (client) =>
          client.clientName && client.clientName.toLowerCase().includes(filter.value.toLowerCase()),
      ) || []
})

const updateModel = (client: Client) => {
  filter.value = client.clientName
  model.value = client
  isActive.value = false
  emits('change')
}

const openMenu = () => {
  isActive.value = true
  filter.value = ''
}

const closeMenu = () => {
  isActive.value = false
  loadDefault()
}

const toggleMenu = () => {
  isActive.value = !isActive.value
  if (isActive.value) {
    filter.value = ''
  } else {
    loadDefault()
  }
}

const setValues = (event: Event) => {
  filter.value = (event.target as HTMLInputElement).value
  isActive.value = true
}

watch(model, () => loadDefault(), { immediate: true })
</script>

<template>
  <div class="relative" v-on-click-outside="() => closeMenu()" v-bind="$attrs">
    <div class="flex items-center border border-white dark:border-gray-400">
      <input
        type="text"
        autocomplete="off"
        spellcheck="false"
        class="text-sm text-white outline-none px-2 py-1 w-full"
        :id="name"
        :name="$t('glossary.client')"
        :title="title"
        :value="filter"
        :disabled="clients.length === 1"
        @input="(e) => setValues(e)"
        @click="openMenu()"
      />
      <div class="flex items-center me-2">
        <BaseButton v-if="clients.length !== 1" :title="$t('common.open')" @click="toggleMenu()">
          <CaretIcon :active="isActive" />
        </BaseButton>
      </div>
    </div>
    <BaseDropdown :open="isActive">
      <ul v-for="(client, index) in filteredOptions" :key="index" class="cursor-pointer text-sm">
        <li
          class="flex items-center text-sm truncate dark:text-gray-300 hover:bg-gray-100 dark:hover:text-white dark:hover:bg-gray-800 p-2"
          @click="updateModel(client)"
        >
          {{ client.clientName }}
        </li>
      </ul>
      <div
        v-if="filteredOptions?.length === 0"
        class="px-2 py-1.5 font-light text-sm dark:text-gray-400"
      >
        {{ $t('message.noResult') }}
      </div>
    </BaseDropdown>
  </div>
</template>
